import type { ReactElement } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";

import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 送ったメール 1 通（<c>GET /api/admin-center/mails/{id}</c>。サーバの CenterMailViewModel と同じ欄）。 */
export interface CenterMail {
    readonly sentMailId: number;
    readonly tenantId: number;
    readonly hasTenant: boolean;
    readonly sender: string;
    readonly recipient: string;
    readonly subject: string;
    /** テキスト本文。<b>鍵はサーバで伏せてある。</b>文字として出す（HTML として描かない）。 */
    readonly body: string;
    readonly redacted: boolean;
    readonly sentAt: string;
    readonly statusName: string;
    readonly statusNote: string;
    readonly statusCode: string;
    readonly providerMessageId: string;
    readonly error: string;
    readonly completedAt: string;
    readonly failed: boolean;
    readonly deliveryName: string;
    readonly deliveryNote: string;
    readonly deliveredAt: string;
    readonly deliveryDetail: string;
    readonly undelivered: boolean;
}

/** 送信（渡せたか）と配送（届いたか）は別物。<b>分けて出す。</b> */
function Outcome({ warn, title, note, detail }: {
    readonly warn: boolean;
    readonly title: string;
    readonly note: string;
    readonly detail: string;
}): ReactElement {
    return (
        <div className={`${warn ? "alert-warning" : "panel-message"} mb-4 p-4`}>
            <strong>{title}</strong>
            <p className="mt-1 text-sm">{note}</p>
            {detail.length > 0 ? <p className="mt-2 whitespace-pre-line font-mono text-xs">{detail}</p> : null}
        </div>
    );
}

function Item({ label, className, children }: {
    readonly label: string;
    readonly className?: string;
    readonly children: ReactElement | string | number;
}): ReactElement {
    return <div><dt className="text-xs text-slate-500">{label}</dt><dd className={className}>{children}</dd></div>;
}

function SendRecord({ mail }: { readonly mail: CenterMail }): ReactElement {
    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">送信の記録</h2>

            {/* 「受け付けられた」を「届いた」と読ませない。ここが切り分けの要 */}
            <Outcome warn={mail.failed} title={`送信: ${mail.statusName}`} note={mail.statusNote} detail={mail.error} />
            <Outcome
                warn={mail.undelivered} title={`配送: ${mail.deliveryName}`}
                note={mail.deliveryNote} detail={mail.deliveryDetail}
            />

            <dl className="grid gap-3 sm:grid-cols-2">
                <Item label="宛先">{mail.recipient}</Item>
                <Item label="差出人">{mail.sender}</Item>
                <Item label="契約 ID">
                    {mail.hasTenant
                        ? <Link to={adminCenterUrls.tenant(mail.tenantId)} className="btn-secondary btn-sm">{mail.tenantId}</Link>
                        : <span className="badge-muted">契約前の送信</span>}
                </Item>
                <Item label="記録の番号" className="font-mono">{mail.sentMailId}</Item>
                <Item label="結果を記録した時刻">{mail.completedAt}</Item>
                <Item label="配送の通知が指す時刻">{mail.deliveredAt}</Item>
                <Item label="SendGrid の応答" className="font-mono">{mail.statusCode}</Item>
                {/* 「送ったはずだが届かない」を SendGrid に調べてもらうにはこれが要る */}
                <Item label="SendGrid の送信番号" className="font-mono break-all">{mail.providerMessageId}</Item>
            </dl>
        </section>
    );
}

/**
 * 送ったメール 1 通。
 *
 * <b>本文は文字として出す。</b>記録に残るのはテキスト版だけで、
 * エスケープしたまま <c>whitespace-pre-line</c> で見せる。HTML として描くと、
 * 宛先が書いた文字（表示名など）がそのまま管理者の画面で動く。
 */
export function MailPage(): ReactElement | null {
    const [search] = useSearchParams();
    const { id: pathId } = useParams();
    const id = pathId ?? queryValue(search, "id") ?? "";
    const data = useApiData<CenterMail>(adminCenterApi.mail(id));
    usePageMeta(titleFor(data, `送ったメール ${id}`));

    if (data.state !== "ready") {
        return <ApiState data={data} what="送ったメール" />;
    }
    const mail = data.value;

    return (
        <div className="page max-w-3xl">
            <p className="mb-4"><Link to={adminCenterUrls.mails} className="btn-secondary btn-sm">一覧へ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title">{mail.subject}</h1>
                <p className="page-lead">{mail.sentAt} に送信</p>

                <SendRecord mail={mail} />

                <section className="card p-6">
                    <h2 className="section-title">本文</h2>
                    {/* 記録に残るのはテキスト版だけ。HTML 版は MailLogs に入らない */}
                    <p className="mb-4 text-sm text-slate-500">テキスト版です。HTML 版は記録されていません。</p>

                    {mail.redacted
                        ? (
                            <div className="alert-warning mb-4">
                                <strong>リンクの鍵を伏せて表示しています。</strong>
                                パスワード再設定・アドレス変更・招待のリンクは、期限内であれば
                                開いた人がその利用者になれてしまいます。記録そのものは変えていません。
                            </div>
                        )
                        : null}

                    <div className="whitespace-pre-line rounded bg-slate-50 p-4 text-sm" data-mail-body="">{mail.body}</div>
                </section>
            </div>
        </div>
    );
}
